// FUDR-13118: persist last-active + run flow-aware reset on foreground / restore

import { get } from 'lodash';

import {
  appMaxInactiveTimeToReloadInMNS,
  storageKeys,
  storageTypes,
} from '../constants/globals';
import history from '../helpers/history';
import {
  clearSelectedTable as clearAppSelectedTable,
  clearViewCartItems as clearAppViewCartItems,
  dispatchUserCartAction,
} from '../actions/app.action';
import {
  clearSelectedTable as clearOrderingSelectedTable,
  clearViewCartItems as clearOrderingViewCartItems,
  clearSelectedAddress as clearOrderingSelectedAddress,
} from '../actions/ordering';
import { getItem, setItem } from './storage';
import { isNativeWebView } from './helpers';
import { beginSuppressCartPersistAfterReset } from './inactivityResetCartPersistGate';
import {
  buildInactivityResetPlan,
  inactivityFlows,
  inactivityResetSkipReasons,
  isInactivityThresholdExceeded,
} from './inactivityReset';
import { clearBoundDeliveryLocationSession } from '../containers/Site/outlet/siteDeliveryLocation';

let lastActiveAtRef = 0;
let inactivityResetInProgress = false;
let inactivityListenersBound = false;

// Re-export so existing MenuPage imports keep working if any still point here.
export {
  shouldSuppressCartPersistAfterInactivityReset,
  beginSuppressCartPersistAfterReset,
  endSuppressCartPersistAfterReset,
} from './inactivityResetCartPersistGate';

const clearOrderingSession = (store) => {
  store.dispatch(clearOrderingSelectedTable());
  store.dispatch(clearOrderingViewCartItems());
  store.dispatch(clearOrderingSelectedAddress());
  clearBoundDeliveryLocationSession();
};

const readPersistedLastActive = () => {
  const stored = getItem(storageTypes.sessionStorage, storageKeys.lastActiveAt);
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export const persistLastActive = (timestamp = Date.now()) => {
  const value = Number(timestamp);
  if (!Number.isFinite(value) || value <= 0) {
    return;
  }
  lastActiveAtRef = value;
  setItem(storageTypes.sessionStorage, storageKeys.lastActiveAt, value);
};

export const getLastActiveAt = () => lastActiveAtRef || readPersistedLastActive();

export const runWhenPersistorBootstrapped = (persistor, callback) => {
  if (!persistor || typeof persistor.getState !== 'function') {
    callback();
    return;
  }
  if (persistor.getState().bootstrapped) {
    callback();
    return;
  }
  let unsubscribe;
  unsubscribe = persistor.subscribe(() => {
    if (persistor.getState().bootstrapped) {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      callback();
    }
  });
};

const applyFlowAwareReset = (store, pathname) => {
  if (inactivityResetInProgress || !store) {
    return false;
  }

  const plan = buildInactivityResetPlan({
    pathname,
    state: store.getState(),
  });

  if (!plan.apply) {
    return false;
  }

  inactivityResetInProgress = true;

  try {
    if (
      plan.flow === inactivityFlows.ordering ||
      plan.flow === inactivityFlows.rewards
    ) {
      // Ordering and rewards both clear the ordering session; rewards may redirect
      // nested /app/ordering/... under the reward app reset → /app/home.
      // Sticky suppress (no timed clear): covers React 18 batched unmount + saveCart
      // cleanups after history.push. Cart pages call endSuppress on mount.
      beginSuppressCartPersistAfterReset();
      clearOrderingSession(store);
      if (plan.redirectTo && history && typeof history.push === 'function') {
        history.push(plan.redirectTo);
      }
    } else if (plan.flow === inactivityFlows.restaurant) {
      // Same sticky suppress as ordering/rewards: unmounting MenuPage/Cart must not
      // re-persist cleared app cart items via saveCart cleanups after history.push.
      beginSuppressCartPersistAfterReset();
      store.dispatch(clearAppSelectedTable());
      store.dispatch(clearAppViewCartItems());
      store.dispatch(dispatchUserCartAction({}));
      if (plan.redirectTo && history && typeof history.push === 'function') {
        history.push(plan.redirectTo);
      }
    } else if (plan.redirectTo && history && typeof history.push === 'function') {
      history.push(plan.redirectTo);
    }

    persistLastActive(Date.now());
    return true;
  } finally {
    inactivityResetInProgress = false;
  }
};

export const maybeApplyInactivityReset = (store, { now = Date.now(), pathname } = {}) => {
  if (!store || appMaxInactiveTimeToReloadInMNS <= 0) {
    return false;
  }

  const currentPath =
    pathname ||
    (typeof window !== 'undefined' ? get(window, 'location.pathname', '') : '');
  const lastActiveAt = getLastActiveAt();

  if (!isInactivityThresholdExceeded(lastActiveAt, appMaxInactiveTimeToReloadInMNS, now)) {
    persistLastActive(now);
    return false;
  }

  const plan = buildInactivityResetPlan({
    pathname: currentPath,
    state: store.getState(),
  });

  // Payment / confirmation return from a gateway can exceed the threshold; do not
  // leave a stale timestamp that would reset the next non-payment screen.
  if (!plan.apply) {
    if (plan.reason === inactivityResetSkipReasons.excludedRoute) {
      persistLastActive(now);
    }
    return false;
  }

  return applyFlowAwareReset(store, currentPath);
};

const handlePageHidden = () => {
  persistLastActive(Date.now());
};

const handlePageShown = (store, persistor) => {
  runWhenPersistorBootstrapped(persistor, () => {
    maybeApplyInactivityReset(store);
  });
};

/**
 * Global inactivity tracking for web (not native WebView — native already resets).
 * Call once at app boot after the store exists.
 */
export const activateInactivityReset = ({ store, persistor } = {}) => {
  if (typeof window === 'undefined' || !store) {
    return;
  }
  if (isNativeWebView || appMaxInactiveTimeToReloadInMNS <= 0) {
    return;
  }
  if (inactivityListenersBound) {
    return;
  }
  inactivityListenersBound = true;

  const existingLastActive = readPersistedLastActive();
  if (existingLastActive) {
    lastActiveAtRef = existingLastActive;
  } else {
    persistLastActive(Date.now());
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      handlePageHidden();
      return;
    }
    if (document.visibilityState === 'visible') {
      handlePageShown(store, persistor);
    }
  });

  window.addEventListener('blur', () => {
    handlePageHidden();
  });

  window.addEventListener('focus', () => {
    if (document.visibilityState === 'visible') {
      handlePageShown(store, persistor);
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event && event.persisted) {
      handlePageShown(store, persistor);
    }
  });

  runWhenPersistorBootstrapped(persistor, () => {
    maybeApplyInactivityReset(store);
  });
};
